import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useRemovePlmEventResponseMutation,
  usePlmEventResponseListQuery,
  usePlmEventResponseListSubSubscription,
  EnumPlmEventResponseResponseStatus,
} from '../../../../types/generated-types';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Add,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { Notifier } from '../../../system/services/notificationManager';
import {
  useInjectableComponents,
  BottomMenuItem,
} from '../../../system/services/injectableComponentsManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import { humanize } from '../../../ui/shell/utilities';

type PlmEventResponseItem = NonNullable<
  NonNullable<
    ReturnType<typeof usePlmEventResponseListQuery>['data']
  >['plmEventResponses']
>[0];

export const PlmEventResponseList = () => {
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const { setContextMenuItems, setTitle, setTitlePath } =
    useInjectableComponents();
  const [plmEventResponses, setPlmEventResponses] = useState<
    PlmEventResponseItem[]
  >([]);

  const { data, loading, error } = usePlmEventResponseListQuery({
    fetchPolicy: 'network-only',
  });
  const [deletePlmEventResponse] = useRemovePlmEventResponseMutation();

  // Update local state when data changes
  useEffect(() => {
    if (data?.plmEventResponses) {
      setPlmEventResponses(data.plmEventResponses);
    }
  }, [data]);

  // Subscribe to real-time updates
  usePlmEventResponseListSubSubscription({
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (error) {
      Notifier.error('There was a problem fetching the PLM Responses: ', error);
    }
  }, [error]);

  const handleRemovePlmEventResponse = useCallback(
    async (id: string) => {
      if (!can('delete', 'PlmEventResponse')) {
        Notifier.error('You do not have permission to delete PLM Responses');
        return;
      }

      try {
        await deletePlmEventResponse({
          variables: { id },
        });
        Notifier.success('PLM event response deleted successfully');
      } catch (error) {
        Notifier.error('Failed to delete PLM event response');
      }
    },
    [deletePlmEventResponse, can],
  );

  const handleViewPlmEventResponse = useCallback(
    (id: string) => {
      navigate(`/plm-event-responses/${id}`);
    },
    [navigate],
  );

  const handleEditPlmEventResponse = useCallback(
    (id: string) => {
      navigate(`/plm-event-responses/${id}/edit`);
    },
    [navigate],
  );

  const handleAddPlmEventResponse = useCallback(() => {
    navigate('/plm-event-responses/add');
  }, [navigate]);

  const handleViewTimeline = useCallback(
    (response: PlmEventResponseItem) => {
      navigate(
        `/plm/event-responses/${response._id}/timeline?returnTo=/plm-event-responses`,
      );
    },
    [navigate],
  );

  const isSmall = useIsSmallScreen();

  // Set title and breadcrumb
  useEffect(() => {
    setTitle(isSmall ? 'PLM Responses' : 'Peak Load Management Responses');
    setTitlePath('/plm-event-responses');
  }, [setTitle, setTitlePath, isSmall]);

  // Set context menu items
  useEffect(() => {
    const menuItems: BottomMenuItem[] = [];

    if (can('create', 'PlmEventResponse')) {
      menuItems.push({
        id: 'add-plm-event-response',
        label: isSmall
          ? 'Add PLM Event Response'
          : 'Add Peak Load Management Event Response',
        icon: <Add />,
        action: handleAddPlmEventResponse,
      });
    }

    setContextMenuItems([{ items: menuItems }]);
  }, [can, handleAddPlmEventResponse, setContextMenuItems, isSmall]);

  const columns: MRT_ColumnDef<PlmEventResponseItem>[] = [
    {
      accessorKey: '_id',
      header: 'ID',
    },
    {
      accessorKey: 'event.program.name',
      header: 'Program',
      Cell: ({ row }) => {
        const programName = row.original.event?.program?.name;
        return programName || '-';
      },
    },
    {
      accessorKey: 'curtailmentPlan.name',
      header: 'Curtailment Plan',
      Cell: ({ row }) => {
        const planName = row.original.curtailmentPlan?.name;
        return planName || '-';
      },
    },
    {
      accessorKey: 'scheduledStart',
      header: 'Start',
      Cell: ({ row }) => {
        const actualStart = row.original.actualStart;
        const scheduledStart = row.original.scheduledStart;
        const startTime = actualStart || scheduledStart;
        return startTime ? new Date(startTime).toLocaleString() : '-';
      },
    },
    {
      accessorKey: 'scheduledEnd',
      header: 'End',
      Cell: ({ row }) => {
        const actualEnd = row.original.actualEnd;
        const scheduledEnd = row.original.scheduledEnd;
        const endTime = actualEnd || scheduledEnd;
        return endTime ? new Date(endTime).toLocaleString() : '-';
      },
    },
    {
      accessorKey: 'responseSentAt',
      header: 'Response Sent At',
      Cell: ({ cell }) => {
        const sentAt = cell.getValue<number>();
        return sentAt ? new Date(sentAt).toLocaleString() : '-';
      },
    },
    {
      accessorKey: 'responseStatus',
      header: 'Response Status',
      Cell: ({ cell }) => {
        const status = cell.getValue<EnumPlmEventResponseResponseStatus>();
        if (!status) return '-';

        const getStatusColor = (status: EnumPlmEventResponseResponseStatus) => {
          switch (status) {
            case EnumPlmEventResponseResponseStatus.Completed:
              return '#4caf50';
            case EnumPlmEventResponseResponseStatus.Active:
              return '#2196f3';
            case EnumPlmEventResponseResponseStatus.Declined:
              return '#f44336';
            case EnumPlmEventResponseResponseStatus.PendingApproval:
              return '#ff9800';
            case EnumPlmEventResponseResponseStatus.Scheduled:
              return '#9c27b0';
            case EnumPlmEventResponseResponseStatus.Suspended:
              return '#ff5722';
            case EnumPlmEventResponseResponseStatus.Resumed:
              return '#00bcd4';
            default:
              return '#757575';
          }
        };

        return (
          <Box
            sx={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold',
              backgroundColor: getStatusColor(status),
              color: 'white',
              display: 'inline-block',
            }}
          >
            {humanize(`${status}`)}
          </Box>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      Cell: ({ cell }) => {
        const createdAt = cell.getValue<number>();
        return createdAt ? new Date(createdAt).toLocaleString() : '-';
      },
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated At',
      Cell: ({ cell }) => {
        const updatedAt = cell.getValue<number>();
        return updatedAt ? new Date(updatedAt).toLocaleString() : '-';
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 200,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title="View Timeline">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleViewTimeline(row.original);
              }}
              size="small"
              sx={{ padding: '4px' }}
            >
              <TimelineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleViewPlmEventResponse(row.original._id);
              }}
              size="small"
              sx={{ padding: '4px' }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleEditPlmEventResponse(row.original._id);
              }}
              disabled={!can('update', 'PlmEventResponse')}
              size="small"
              sx={{ padding: '4px' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleRemovePlmEventResponse(row.original._id);
              }}
              disabled={!can('delete', 'PlmEventResponse')}
              size="small"
              sx={{ padding: '4px' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (error) {
    return <div>Error loading PLM Responses: {error.message}</div>;
  }

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box width={'100%'}>
      <MaterialReactTable
        columns={columns}
        data={plmEventResponses}
        defaultColumn={{ size: 125 }}
        enableRowSelection={false}
        enableColumnOrdering={false}
        enableSorting={false}
        enableFilters={false}
        enableColumnFilters={false}
        enableGlobalFilter
        enableColumnResizing
        enableDensityToggle
        enableFullScreenToggle={false}
        muiTableProps={{
          sx: {
            tableLayout: 'auto',
          },
        }}
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => handleViewPlmEventResponse(row.original._id),
          sx: {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        })}
        state={{
          isLoading: loading,
          columnVisibility: {
            _id: false,
            createdAt: false,
            updatedAt: false,
            responseSentAt: false,
          },
        }}
        layoutMode="semantic"
      />
    </Box>
  );
};
